/**
 * Opt-in transition timing / double-event logging.
 *
 * Enable (persists in this tab until you turn it off):
 *   - `?transitionDebug=1` or `localStorage.setItem('transitionDebug', '1')` + reload
 * Disable: `?transitionDebug=0` or `localStorage.removeItem('transitionDebug')` + reload
 *
 * **Across full page loads:** the last ~120 events are written to `sessionStorage`, so
 * the ring survives `/` → `/chotbat/` and back. `tms` is ms from the first log in *this
 * tab session* (stays one timeline), not per-page. Use:
 *   - `__transitionDebug.dump()` — full JSON to paste
 *   - `__transitionDebug.print()` — table
 *   - `__transitionDebug.clearRing()` — memory + `sessionStorage` (optional fresh timeline)
 *     then call `__transitionDebug.resetSession()` to reset the session clock
 *
 * **Pixel/flash** between loader and first paint: watch `loader_dismiss_done` →
 * `transitionIn:*` and compare to what you *see*; a gap there often means a paint
 * (blank/static) before the shader is ready. DevTools: Performance + Screenshots, or
 * “Disable cache” off + Slow 3G to exaggerate.
 */

const LS_KEY = 'transitionDebug';
const SS_RING_KEY = 'transitionDebugRing';
const SS_T0_KEY = 'transitionDebugT0';
const RING_MAX = 120;
const _ring = [];

let _sessionT0 = null; // wall ms at start of this tab's debug session
let _restored = false;
let _synced = false;
let _attachOnce = false;

function syncFromUrl() {
  if (typeof window === 'undefined' || _synced) return;
  _synced = true;
  try {
    const p = new URLSearchParams(window.location.search).get('transitionDebug');
    if (p === '1' || p === 'true') {
      localStorage.setItem(LS_KEY, '1');
    } else if (p === '0' || p === 'false') {
      localStorage.removeItem(LS_KEY);
    }
  } catch {
    /* ignore */
  }
}

function getSessionT0() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    if (_sessionT0 == null) _sessionT0 = Date.now();
    return _sessionT0;
  }
  if (_sessionT0 != null) return _sessionT0;
  try {
    const n = sessionStorage.getItem(SS_T0_KEY);
    if (n != null) {
      const v = parseInt(n, 10);
      if (!Number.isNaN(v)) {
        _sessionT0 = v;
        return _sessionT0;
      }
    }
  } catch {
    /* ignore */
  }
  _sessionT0 = Date.now();
  try {
    sessionStorage.setItem(SS_T0_KEY, String(_sessionT0));
  } catch {
    /* ignore */
  }
  return _sessionT0;
}

function restoreRing() {
  if (typeof window === 'undefined' || _restored) return;
  _restored = true;
  if (typeof sessionStorage === 'undefined') return;
  try {
    const j = sessionStorage.getItem(SS_RING_KEY);
    if (j) {
      const a = JSON.parse(j);
      if (Array.isArray(a)) {
        _ring.length = 0;
        a.slice(-RING_MAX).forEach((e) => _ring.push(e));
      }
    }
  } catch {
    /* ignore */
  }
}

function persistRing() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(SS_RING_KEY, JSON.stringify(_ring));
  } catch {
    /* ignore */
  }
}

/**
 * @returns {string} ms from first debug log in this tab (survives navigations)
 */
function nowMs() {
  return (Date.now() - getSessionT0()).toFixed(1);
}

/**
 * @returns {boolean}
 */
export function isTransitionDebug() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }
  syncFromUrl();
  return localStorage.getItem(LS_KEY) === '1';
}

/**
 * Multiplies in-place card grow + pixel-reveal durations. Set in DevTools before
 * opening a world, e.g. `window.__REVEAL_SLOW = 4` (then run the transition).
 * Prefer this over Performance **CPU throttling**, which can starve rAF/GSAP and
 * make the effect look like it never runs. Reset: `delete window.__REVEAL_SLOW` or `= 1`.
 *
 * @returns {number}
 */
export function getRevealSlowMult() {
  if (typeof window === 'undefined') return 1;
  const m = Number(window.__REVEAL_SLOW);
  return Number.isFinite(m) && m > 0 ? m : 1;
}

/**
 * @param {string} phase
 * @param {Record<string, unknown>} [detail]
 */
export function transitionLog(phase, detail) {
  if (!isTransitionDebug()) return;
  restoreRing();
  getSessionT0();
  const t = nowMs();
  // Spread detail first; reserved keys win so e.g. `at: 1.1` from a caller
  // cannot replace the wall-clock `at` string.
  const row = {
    ...detail,
    tms: t,
    phase,
    at: new Date().toISOString(),
    page: typeof location !== 'undefined' ? location.href : '',
  };
  _ring.push(row);
  if (_ring.length > RING_MAX) _ring.shift();
  persistRing();
  const extra = detail && Object.keys(detail).length
    ? ` ${JSON.stringify(detail)}`
    : '';
  console.log(`%c[transition] +${t}ms`, 'color:#7c9cff', phase, extra || '');

  if (!_attachOnce && typeof window !== 'undefined') {
    _attachOnce = true;
    window.__transitionDebug = {
      isEnabled: () => isTransitionDebug(),
      getRing: () => [..._ring],
      clearRing: () => {
        _ring.length = 0;
        try {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SS_RING_KEY);
          }
        } catch {
          /* ignore */
        }
      },
      /** Clear ring + session clock; next log starts `tms` at ~0 again. */
      resetSession: () => {
        _ring.length = 0;
        _sessionT0 = null;
        _restored = true;
        try {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SS_RING_KEY);
            sessionStorage.removeItem(SS_T0_KEY);
          }
        } catch {
          /* ignore */
        }
      },
      dump: () => JSON.stringify(_ring, null, 2),
      /** Promise — copies `dump()` to the clipboard (user gesture may be required in some browsers). */
      copy: () => {
        const s = JSON.stringify(_ring, null, 2);
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          return navigator.clipboard.writeText(s).then(() => {
            // eslint-disable-next-line no-console
            console.info('[transition] copied', _ring.length, 'events to clipboard');
          });
        }
        return Promise.reject(new Error('clipboard not available'));
      },
      print: () => console.table(_ring),
      disable: () => {
        localStorage.removeItem(LS_KEY);
        try {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SS_RING_KEY);
            sessionStorage.removeItem(SS_T0_KEY);
          }
        } catch {
          /* ignore */
        }
        if (location.search.includes('transitionDebug=')) {
          const u = new URL(location.href);
          u.searchParams.delete('transitionDebug');
          return u.toString();
        }
        return null;
      },
    };
    // eslint-disable-next-line no-console
    console.info(
      '[transition] debug on — `__transitionDebug.print()` or `.dump()` to export'
    );
  }
}
